<template>
  <svg :width="`${widthInInches}in`" :height="`${heightInInches}in`" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <!-- Define a mask using the PNG -->
      <mask :id="`${uuid}-pngMask`">
        <!-- Use the PNG image to define the mask -->
        <image
          :href="processedImageUrl"
          :x="artworkOffsetX"
          :y="artworkOffsetY"
          :width="artworkWidthInInches"
          :height="artworkHeightInInches"
          preserveAspectRatio="none"
        />
      </mask>

      <!-- Define a filter for the drop shadow -->
      <filter :id="`${uuid}-dropShadow`" x="0" y="0" :width="widthInInches" :height="heightInInches" filterUnits="userSpaceOnUse">
        <feGaussianBlur in="SourceAlpha" stdDeviation="0.1" />
        <feOffset dx="-0.1" dy="0.1" result="offsetblur" />
        <feMerge>
          <feMergeNode in="offsetblur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    <!-- Backing board component -->
    <RenderSvgBackingBoard
      :frameSpec="frameSpec"
      :widthInInches="widthInInches"
      :heightInInches="heightInInches"
      :x="0"
      :y="0"
    />
    

    <!-- Apply the mask and drop shadow to the shape -->
    <g v-if="elevatedMount" :style="`filter: url(#${uuid}-dropShadow)`">
      <rect
        :x="artworkOffsetX"
        :y="artworkOffsetY"
        :width="artworkWidthInInches"
        :height="artworkHeightInInches"
        :mask="`url(#${uuid}-pngMask)`"
        fill="rgb(0, 0, 0, 0.5)"
      />
    </g>

    <!-- Artwork component -->
    <RenderSvgArtwork :frameSpec="frameSpec" :x="artworkOffsetX" :y="artworkOffsetY" :allowUpload="allowUpload" @upload-click="$emit('upload-click')"/>

    <!-- Mat components -->
    <RenderSvgMat
      v-for="(mat, index) in frameSpec.mats.slice().reverse()"
      :key="index"
      :frameSpec="frameSpec"
      :x="0"
      :y="0"
      :mat="mat"
      :matIndex="index"
    />
  </svg>
</template>

<script>
import RenderSvgArtwork from './RenderSvgArtwork.vue';
import RenderSvgMat from './RenderSvgMat.vue';
import RenderSvgBackingBoard from './RenderSvgBackingBoard.vue';
import { defaultMatThickness } from "../../hardCodedValues";
import isEmpty from "lodash/isEmpty";
import uuidv4 from "uuid/v4";

export default {
  components: {
    RenderSvgArtwork,
    RenderSvgMat,
    RenderSvgBackingBoard
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    allowUpload: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      processedImageUrl: "", // Store the processed image URL
    };
  },
  watch: {
    imageUrl: {
      immediate: true,
      async handler(newUrl) {
        if (newUrl) {
          this.processedImageUrl = await this.createBlackAndWhiteImage(newUrl);
        } else {
          this.processedImageUrl = "";
        }
      },
    },
  },
  computed: {
    uuid() {
      return uuidv4();
    },
    artwork() {
      return this.frameSpec.getArtwork() || {};
    },
    elevatedMount() {
      return this.frameSpec.mount.elevated;
    },
    mat() {
      return this.frameSpec.mats[0] || {};
    },
    imageIsPng() {
      if (!this.frameSpec) return "";

      return this.artwork.customerSpecified && this.artwork.customerSpecified.url.includes(".png");
    },
    imageIsJpg() {
      if (!this.frameSpec) return "";

      return this.artwork.customerSpecified && this.artwork.customerSpecified.url.includes(".jpeg");
    },
    imageUrl() {
      if (!this.frameSpec || !this.artwork) return "";

      if ((this.imageIsPng && this.imageIsJpg) || !this.artwork.customerSpecified) {
        return this.artwork.croppedAndNormalized ? this.artwork.croppedAndNormalized.url : "";
      } else {
        return this.artwork.customerSpecified ? this.artwork.customerSpecified.url : "";
      }
    },
    isCanvasMoulding() {
      return this.frameSpec.isCanvas();
    },
    totalsOfAllMatRevealsOrMountReveals() {
      if (!isEmpty(this.mat)) {
        return {
          topInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
          rightInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
          bottomInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
          leftInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
        };
      } else if (this.frameSpec.isFloated() || this.frameSpec.isClearFloated()) {
        return this.frameSpec.mount.reveal;
      } else {
        return {
          topInInches: 0,
          rightInInches: 0,
          bottomInInches: 0,
          leftInInches: 0
        };
      }
    },
    widthInInches() {
      if (!this.artwork.exterior) return '0';
      return this.artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatRevealsOrMountReveals.leftInInches + this.totalsOfAllMatRevealsOrMountReveals.rightInInches;
    },
    heightInInches() {
      if (!this.artwork.exterior) return '0';
      return this.artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatRevealsOrMountReveals.topInInches + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches;
    },
    artworkWidthInInches() {
      return this.artwork.exterior.widthInInches;
    },
    artworkHeightInInches() {
      return this.artwork.exterior.heightInInches;
    },
    artworkOffsetX() {
      if (this.isCanvasMoulding) {
        return this.totalsOfAllMatRevealsOrMountReveals.leftInInches;
      } else {
        return this.totalsOfAllMatRevealsOrMountReveals.leftInInches - defaultMatThickness;
      }
    },
    artworkOffsetY() {
      if (this.isCanvasMoulding) {
        return this.totalsOfAllMatRevealsOrMountReveals.topInInches;
      } else {
        return this.totalsOfAllMatRevealsOrMountReveals.topInInches - defaultMatThickness;
      }
    }
  },
  methods: {
    async createBlackAndWhiteImage(url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "Anonymous"; // Allow cross-origin image processing
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          const imageData = ctx.getImageData(0, 0, img.width, img.height);
          const pixels = imageData.data;

          for (let i = 0; i < pixels.length; i += 4) {
            const alpha = pixels[i + 3]; // Alpha channel
            if (alpha === 0) {
              // Transparent pixel -> white
              pixels[i] = 0; // Red
              pixels[i + 1] = 0; // Green
              pixels[i + 2] = 0; // Blue
              pixels[i + 3] = 255; // Fully opaque
            } else {
              // Solid pixel -> black
              pixels[i] = 255; // Red
              pixels[i + 1] = 255; // Green
              pixels[i + 2] = 255; // Blue
              pixels[i + 3] = 255; // Fully opaque
            }
          }

          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL()); // Return the modified image as a data URL
        };
        img.onerror = () => resolve(""); // Handle errors gracefully
        img.src = url;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
svg {
  align-items: center;
  display: flex;
  height: auto;
  justify-content: center;
  max-width: 100%;
  width: 100%;
}
</style>